import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import { HelpIcon } from "assets/icons/system";
import { Card, CardContent } from "components/ui/Card";

import { formatNumberToWonCurrency } from "utils/format";
import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";
import { useSalesDetailQuery } from "queries/Business/Dashboard/useDashboardQueries";
import {
  getSalesApiPassType,
  salesDashboardTabOptions,
} from "constants/businessDashboard";
import type { SalesDetailParams } from "api/businessDashboard";

import { SalesChangeIndicator } from "./sales-change-indicator";
import { SalesDashboardAverageSummarySkeleton } from "./sales-average-summary.skeleton";
import { SalesDashboardAverageSummaryError } from "./sales-average-summary.error";
import { BusinessDashboardHelpHoverCard } from "components/businessDashboard/ui-kit/help-modal";

dayjs.locale("ko");

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #e3e7ef;
  width: calc(100% - 32px);
  margin: 0 auto;
`;

const StyledCard = styled(Card)`
  border-radius: 0;
  border: none;
  box-shadow: none;
  padding: 0;
  background: #f9fafb;
`;

const StyledCardContent = styled(CardContent)`
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background: #f9fafb;
  height: 46px;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
`;

const ColorBox = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  background: ${({ color }) => color};
  border-radius: 4px;
  margin-right: 6px;
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.2px;
  color: #7b819a;
  margin-right: 4px;
`;

const HelpButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  margin-right: 8px;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;

  &:hover {
    color: #fff;
  }
`;

const RightGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Value = styled.span`
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.2px;
  font-weight: 700;
  color: #202020;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #eaedf2;
  margin: 8px 0;
`;

export const SalesDashboardAverageSummary = () => {
  const { searchParams } = useUpdateUrlParams();

  const activeTab = searchParams.get("tab") || salesDashboardTabOptions[0].id;
  const passType = getSalesApiPassType(activeTab);

  const referenceMonth = searchParams.get("month") || dayjs().format("YYYY-MM");

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const apiParams = useMemo(() => {
    const params: SalesDetailParams = {
      referenceMonth,
      passType,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [referenceMonth, passType, partnerInfo]);

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useSalesDetailQuery(apiParams);

  if (isDetailLoading) return <SalesDashboardAverageSummarySkeleton />;
  if (isDetailError || !detailData)
    return <SalesDashboardAverageSummaryError />;

  return (
    <Wrapper>
      <StyledCard>
        <StyledCardContent>
          <Row>
            <LeftGroup>
              <ColorBox color="#4785FF" />
              <Label>평균 판매가격</Label>
              <BusinessDashboardHelpHoverCard type="averageSales">
                <HelpButton type="button">
                  <HelpIcon />
                </HelpButton>
              </BusinessDashboardHelpHoverCard>
            </LeftGroup>
            <RightGroup>
              <Value>
                {formatNumberToWonCurrency(
                  detailData.summary.averageSellingPrice.amount
                )}
              </Value>
              <SalesChangeIndicator
                changeRate={detailData.summary.averageSellingPrice.changeRate}
              />
            </RightGroup>
          </Row>
          <Divider />
          <Row style={{ marginBottom: "8px" }}>
            <LeftGroup>
              <ColorBox color="#03921A" />
              <Label>기간제 객단가</Label>
              <BusinessDashboardHelpHoverCard type="periodUnitPrice">
                <HelpButton type="button">
                  <HelpIcon />
                </HelpButton>
              </BusinessDashboardHelpHoverCard>
            </LeftGroup>
            <RightGroup>
              <Value>
                {formatNumberToWonCurrency(
                  detailData.summary.periodBasedUnitPrice.amount
                )}
              </Value>
              <SalesChangeIndicator
                changeRate={detailData.summary.periodBasedUnitPrice.changeRate}
              />
            </RightGroup>
          </Row>
          <Row>
            <LeftGroup>
              <ColorBox color="#8E65E2" />
              <Label>횟수제 객단가</Label>
              <BusinessDashboardHelpHoverCard type="countUnitPrice">
                <HelpButton type="button">
                  <HelpIcon />
                </HelpButton>
              </BusinessDashboardHelpHoverCard>
            </LeftGroup>
            <RightGroup>
              <Value>
                {formatNumberToWonCurrency(
                  detailData.summary.countBasedUnitPrice.amount
                )}
              </Value>
              <SalesChangeIndicator
                changeRate={detailData.summary.countBasedUnitPrice.changeRate}
              />
            </RightGroup>
          </Row>
        </StyledCardContent>
      </StyledCard>
    </Wrapper>
  );
};
